import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import {
  CircleDot, MapPin, Plus, LogOut, Power, KeyRound, Wifi, WifiOff, Check,
  FileText, Copy, ShieldOff, QrCode, ShieldAlert, Unlink, RotateCcw,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import NotaExplicativa from '../ui/NotaExplicativa';
import Motivo from '../experiencia/Motivo';
import Ayuda from '../ui/Ayuda';
import ChipEstado from '../ui/ChipEstado';
import { ESTADO_TERMINAL } from '../ui/estados';
import { fechaYHora } from '../ui/sesiones';
import { MODOS, nombreDelModo, explicacionDelModo } from '../ui/terminales';
import { Cargando } from '../ui/EstadoDePantalla';
import VincularEnLinea from '../terminal/VincularEnLinea';
import LicenciasDeTerminal from '../terminal/LicenciasDeTerminal';
import { MODULO_TERMINALES, contextoDeTerminal } from '../terminal/enlaceDeTerminal';
import { leerErrorDelContrato } from '../terminal/errorDelContrato';
// F5 (DISEÑO-F5 §7.1): estado real, memoria, identificar, estación y «Por revisar».
import { LineaDeEstado, Identificar, ChipsDeFiltro } from '../terminal/EstadoDeTerminal';
import { tieneProblema, esDeProducto as esTerminalDeProducto } from '../terminal/estadoDeTerminalUtil';
import EstacionDeTerminal from '../terminal/EstacionDeTerminal';
import PorRevisar from '../terminal/PorRevisar';
// TA-7 §6 (P-28): «Cambiar de red» en la tarjeta (orden ABRIR_AJUSTES_RED).
import CambiarDeRed from '../terminal/CambiarDeRed';

// TERMINALES DE CONTEO · T-1
//
// QUÉ CONTESTA ESTA PANTALLA, en el orden en que la gente lo pregunta:
//
//   1. «¿Qué terminales tengo y dónde están?»
//   2. «¿Quién está usando esa de ahí ahora mismo?»
//   3. «¿Cómo conecto una nueva?»
//   4. «Esa se rompió / se la llevaron: cómo la quito.»
//
// POR QUÉ NO ESTÁ DENTRO DE «DISPOSITIVOS». Se parecen en el nombre y en
// nada más. «Dispositivos» son las tiras de luz de las repisas: se
// encienden, se apagan, se prueban. Una terminal de conteo tiene
// personas delante y mueve inventario a nombre de alguien. Meterlas
// juntas obligaría a explicar en cada botón a cuál de las dos cosas se
// refiere — que es exactamente el trámite que la doctrina prohíbe.
//
// LAS RETIRADAS SE ENSEÑAN, en gris y al final. Una terminal
// desconectada sigue existiendo; esconderla haría que la única forma de
// reactivarla fuera acordarse de que estuvo ahí.

// LA TERMINAL DE PRODUCTO (F1, 10 oct 2026) ENTRA POR ESTA MISMA PANTALLA.
// «Agregar terminal» abre, encima, el asistente de tres pasos
// (`terminal/VincularTerminal.jsx`); el QR de la terminal (`/t/<código>`)
// llega como `contexto.vincular` y lo abre con el código ya puesto, y
// `/t` a secas como `contexto.agregar`. Lo de v1 —«Conectar una
// terminal», el número de la perilla, los ajustes— sigue igual.
export default function Terminales({ contexto, irA }) {
  // F1c: «Pasar a otra empresa» solo con `terminal.traspasar` (el DUEÑO).
  const auth = useAuth();
  const puedeTraspasar = auth?.puede ? auth.puede('terminal.traspasar') : false;
  // F5: quien administra o supervisa identifica; «Qué se hace aquí» es de quien
  // administra; «Por revisar» lo ve quien supervisa y lo resuelve quien tiene
  // `cuarentena.resolver` (la subpantalla apaga con motivo lo que no toque).
  const puedeAdministrar = auth?.puede ? auth.puede('terminal.administrar') : true;
  const puedeSupervisar = auth?.puede ? auth.puede('terminal.supervisar') : true;
  const puedeResolver = auth?.puede ? auth.puede('cuarentena.resolver') : false;
  const puedeIdentificar = puedeAdministrar || puedeSupervisar;
  const [filtro, setFiltro] = useState('TODAS');
  const [masDe, setMasDe] = useState(null);
  const [estacionDe, setEstacionDe] = useState(null);
  const [revisarDe, setRevisarDe] = useState(null);
  useSubpantalla('estacion', !!estacionDe, () => setEstacionDe(null));
  useSubpantalla('revisar', !!revisarDe, () => setRevisarDe(null));
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [codigoNuevo, setCodigoNuevo] = useState(null);
  const [abierta, setAbierta] = useState(null);
  useSubpantalla('terminal', !!abierta, () => setAbierta(null), { valor: abierta, abrir: setAbierta, reemplaza: true });
  const [vincular, setVincular] = useState(() => {
    const ctx = { ...(contextoDeTerminal(MODULO_TERMINALES, typeof window !== 'undefined' ? window.location.search : '') || {}),
                  ...(contexto || {}) };
    if (ctx.vincular) return { vincular: String(ctx.vincular) };
    if (ctx.agregar) return { desdeEnlace: true, ...(ctx.escrito ? { escrito: String(ctx.escrito) } : {}) };
    return null;
  });
  useSubpantalla('vincular', !!vincular, () => setVincular(null));

  // F1b: cuántas terminales compradas y si se puede conectar otra. El
  // frontend no decide: obedece lo que diga el servidor (D12). `null` = no
  // se pudo leer; el formulario queda apagado con ese motivo.
  const [licencia, setLicencia] = useState(undefined);
  const cargar = useCallback(() => {
    api.get('/dispositivos/terminales')
      .then(setLista)
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
    api.get('/terminales/licencia').then(setLicencia).catch(() => setLicencia(null));
  }, []);

  useEffect(cargar, [cargar]);

  async function guardar(t, cambios, dicho) {
    setError(null);
    try {
      await api.patch(`/dispositivos/terminales/${t.id}`, cambios);
      setAviso(dicho);
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo guardar.');
    }
  }

  async function cerrarSesion(t) {
    setError(null);
    try {
      const d = await api.del(`/dispositivos/sesiones/${t.id_sesion}`);
      setAviso(d.mensaje);
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cerrar.');
    }
  }

  if (estacionDe) {
    return (
      <EstacionDeTerminal
        t={estacionDe}
        onCerrar={() => { setEstacionDe(null); cargar(); }}
        onHecho={(mensaje) => { setAviso(mensaje); setEstacionDe(null); cargar(); }}
      />
    );
  }
  if (revisarDe) {
    return (
      <PorRevisar
        t={revisarDe}
        puedeResolver={puedeResolver}
        onCerrar={() => { setRevisarDe(null); cargar(); }}
      />
    );
  }

  if (vincular) {
    return (
      // 2.11.0 (DISEÑO-ACTIVACION-EN-LINEA D12): «Agregar terminal» es el
      // vínculo en línea; el asistente de antes vive en «Otras formas», sin tarjeta.
      <VincularEnLinea
        contexto={vincular}
        irA={irA}
        onCerrar={() => { setVincular(null); cargar(); }}
      />
    );
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Terminales de conteo</h1>
        <p className="text-sm text-gray-600">
          Los aparatos con perilla que están colgados en las repisas. Aquí ves
          quién está usando cada uno, qué puede hacer, y conectas los nuevos.
        </p>
      </header>

      {aviso && (
        <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setVincular({})}
          className="inline-flex min-h-11 items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
        >
          <QrCode size={16} aria-hidden="true" />
          Agregar terminal
        </button>
        <span className="text-sm text-gray-600">
          Para una terminal de producto: la que enseña un código en su pantalla al encenderla.
        </span>
      </div>

      {licencia?.compradas > 0 && <Ayuda id="terminal.licencia.pantalla" />}
      <LicenciasDeTerminal licencia={licencia} />

      <ConectarTerminal
        licencia={licencia}
        onConectada={(d) => { setCodigoNuevo(d); cargar(); }}
        onError={setError}
      />

      {codigoNuevo && (
        <div className="border-2 border-green-300 bg-green-50 rounded-xl p-4 space-y-2">
          <div className="text-sm font-semibold text-green-900">
            Terminal «{codigoNuevo.nombre}» conectada. Teclea este número en su
            perilla:
          </div>
          <div className="text-4xl font-mono font-bold tracking-[0.3em] text-green-900">
            {codigoNuevo.codigo}
          </div>
          <div className="text-sm text-green-900">
            Girar elige el dígito, apretar pasa al siguiente. Seis apretones.
            <strong> Este número se enseña una sola vez</strong> y caduca en{' '}
            {codigoNuevo.minutos || 10} minutos: si se pierde, se pide otro desde
            la tarjeta de la terminal.
          </div>
          <button
            type="button"
            onClick={() => setCodigoNuevo(null)}
            className="text-sm font-semibold text-green-900 underline"
          >
            Ya lo tecleé, ocultar
          </button>
        </div>
      )}

      {lista === null && <Cargando filas={4} />}

      {/* ESTADO VACÍO ÚTIL: qué es esto, por qué está vacío, qué hacer. */}
      {lista !== null && lista.length === 0 && (
        <div className="border border-gray-200 bg-white rounded-xl p-5 text-center space-y-2">
          <CircleDot className="h-8 w-8 mx-auto text-gray-300" />
          <div className="font-semibold text-gray-900">Sin terminales</div>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Una terminal es un aparato del tamaño de un celular que se cuelga
            junto a una repisa. Muestra lo que hay ahí y deja ajustarlo girando
            una perilla, sin sacar el teléfono ni ir a la computadora.
          </p>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Cuando enciendas una, su pantalla te dará un código: tecléalo aquí o escanéalo.
          </p>
          <button
            type="button"
            onClick={() => setVincular({})}
            className="inline-flex min-h-11 items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
          >
            <QrCode size={16} aria-hidden="true" />
            Agregar terminal
          </button>
        </div>
      )}

      {lista && lista.some(esTerminalDeProducto) && (
        <div className="flex flex-wrap items-center gap-2">
          <ChipsDeFiltro valor={filtro} onCambio={setFiltro} conProblema={lista.filter(tieneProblema).length} />
          <Ayuda id="terminal.estado.pantalla" />
          <span className="text-xs text-gray-600">Cómo se decide el estado <Ayuda id="terminal.estado.ficha" /></span>
        </div>
      )}

      <div className="space-y-3">
        {(lista || []).filter((t) => filtro === 'TODAS' || tieneProblema(t)).map((t) => {
          const editando = abierta === t.id;
          const conEstadoReal = esTerminalDeProducto(t) && !!t.estado_v2 && t.estado !== 'RETIRADA';
          const masAcciones = (
            <>
              <RetirarCredencial
                t={t}
                onHecho={(d) => { setError(null); setAviso(d.mensaje); cargar(); }}
                onError={setError}
              />
              <AccionesDeProducto
                t={t}
                puedeTraspasar={puedeTraspasar}
                onHecho={(d) => { setError(null); setAviso(d.mensaje); cargar(); }}
                onError={setError}
              />
              <Retirar t={t} onGuardar={guardar} />
            </>
          );
          return (
            <div
              key={t.id}
              className={`border rounded-xl p-4 space-y-3 ${
                t.activo ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}
            >
              <div className="flex items-start gap-3">
                <CircleDot className={`h-5 w-5 mt-0.5 shrink-0 ${
                  t.estado === 'TRABAJANDO' ? 'text-green-700' : 'text-gray-400'}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900">{t.nombre}</div>
                  {t.ubicacion && (
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      <MapPin size={13} className="shrink-0" />
                      {t.ubicacion}{t.ubicacion_nombre ? ` · ${t.ubicacion_nombre}` : ''}
                    </div>
                  )}
                  <div className="text-sm text-gray-600">
                    Hace: {nombreDelModo(t.modo_principal)} · se cierra sola a los{' '}
                    {t.inactividad_min} min sin tocarse
                  </div>
                  {/* NUNCA UN ÍCONO VERDE QUE MIENTE: se dice cuándo fue
                      el último latido, no «conectada» a secas. Y una
                      terminal RETIRADA no se presenta como encendida
                      aunque el aparato siga latiendo un rato: el latido
                      verde junto a un chip rojo «Retirada» se contradecía
                      (T-18). Retirada manda: ya no cuenta como en línea. */}
                  {conEstadoReal && <LineaDeEstado t={t} />}
                  {!conEstadoReal && <div className="text-sm text-gray-600 flex items-center gap-1">
                    {t.estado === 'RETIRADA'
                      ? <><WifiOff size={13} className="text-gray-400 shrink-0" /> Retirada: ya no recibe ni mueve nada</>
                      : t.en_linea
                        ? <><Wifi size={13} className="text-green-700 shrink-0" /> Dio señales hace un momento</>
                        : <><WifiOff size={13} className="text-gray-400 shrink-0" />{' '}
                            {t.ultimo_latido
                              ? `Sin señales desde el ${fechaYHora(t.ultimo_latido)}`
                              : 'Todavía no se ha conectado ni una vez'}</>}
                  </div>}
                  {t.estado === 'TRABAJANDO' && (
                    <div className="text-sm text-gray-600">
                      Vinculado desde el {fechaYHora(t.sesion_desde)}
                    </div>
                  )}
                  <AvisoCredencialRetirada t={t} />
                  <DatosDeProducto t={t} />
                </div>
                {/* Verde = alguien la está usando · ámbar = nadie
                    vinculado · rojo = retirada. Los mismos tres colores
                    que en el resto del sistema. */}
                <div className="shrink-0 text-right space-y-0.5">
                  <ChipEstado mapa={ESTADO_TERMINAL} clave={t.estado} />
                  {t.estado !== 'TRABAJANDO' && <Ayuda id="terminales.bloquear" />}
                  {t.estado === 'TRABAJANDO' && (
                    <div className="text-xs font-semibold text-green-800">{t.operador}</div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {t.estado === 'TRABAJANDO' && (
                  <button
                    type="button"
                    onClick={() => cerrarSesion(t)}
                    className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700"
                  >
                    <LogOut size={15} />
                    Cerrar la sesión de {t.operador}
                  </button>
                )}
                {t.activo && (
                  <button
                    type="button"
                    onClick={() => setAbierta(editando ? null : t.id)}
                    className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700"
                  >
                    {editando ? 'Cerrar ajustes' : 'Ajustes'}
                  </button>
                )}
                <PedirCodigo t={t} onCodigo={setCodigoNuevo} onError={setError} />
                <Identificar
                  t={t}
                  puede={puedeIdentificar}
                  onHecho={(d) => { setError(null); setAviso(d.mensaje); }}
                  onError={setError}
                  onRecargar={cargar}
                />
                {/* La confirmación y lo que contestó el servidor se pintan
                    junto al botón, no arriba de la página. */}
                <CambiarDeRed t={t} puede={puedeAdministrar} />
                {esTerminalDeProducto(t) && t.activo && puedeAdministrar && (
                  <button
                    type="button"
                    onClick={() => setEstacionDe(t)}
                    className="inline-flex min-h-11 items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700"
                  >
                    Qué se hace aquí
                  </button>
                )}
                {esTerminalDeProducto(t) && t.activo && (puedeAdministrar || puedeSupervisar) && (
                  <button
                    type="button"
                    onClick={() => setRevisarDe(t)}
                    data-terminal="por-revisar"
                    className={`inline-flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold ${
                      (t.por_revisar || 0) > 0 ? 'border-red-300 bg-red-50 text-red-800' : 'border-gray-300 bg-white text-gray-700'}`}
                  >
                    Por revisar ({t.por_revisar || 0})
                  </button>
                )}
                {esTerminalDeProducto(t) ? (
                  <button
                    type="button"
                    onClick={() => setMasDe(masDe === t.id ? null : t.id)}
                    aria-expanded={masDe === t.id}
                    className="inline-flex min-h-11 items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700"
                  >
                    {masDe === t.id ? 'Menos' : 'Más…'}
                  </button>
                ) : masAcciones}
              </div>
              {/* «Más…» abre DEBAJO, nunca flotante (M1/M6): desvincular, volver a
                  vincular, robada, pasar a otra empresa, retirar su acceso, retirarla. */}
              {esTerminalDeProducto(t) && masDe === t.id && (
                <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3" data-terminal="mas">
                  {masAcciones}
                </div>
              )}

              {editando && <Ajustes t={t} onGuardar={guardar} />}
            </div>
          );
        })}
      </div>

      <NotaExplicativa titulo="Qué pasa cuando nadie está vinculado">
        La terminal sigue encendida y sigue enseñando lo que hay en la repisa,
        pero <strong>no puede mover nada</strong>. En su pantalla dice «Escanea
        para empezar». Es a propósito: cada movimiento tiene que quedar a nombre
        de una persona, no de un aparato colgado en un pasillo.
      </NotaExplicativa>

      <AvisoParaTuPersonal />
    </div>
  );
}

// ---------------------------------------------------------------------
// EL AVISO QUE LE TIENES QUE DAR A TU GENTE · TC-34
//
// POR QUÉ ESTÁ EN ESTA PANTALLA Y NO EN LA DE ACCESO. Los dos textos
// legales de TEGO van en la puerta, porque quien entra está decidiendo
// si los acepta. Éste no es de TEGO con nadie: es el que el DUEÑO le da
// a sus operadores, y aparece aquí porque el momento en que hace falta
// es exactamente éste — el día que cuelga la primera terminal y empieza
// a quedar registrado quién movió qué y a qué hora.
//
// NACE CERRADO. Es un texto largo y no es lo que se viene a hacer a esta
// pantalla; abierto de golpe empujaría hacia abajo las tarjetas de las
// terminales, que sí son el motivo de estar aquí.
// ---------------------------------------------------------------------
function AvisoParaTuPersonal() {
  const [abierto, setAbierto] = useState(false);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!abierto || doc) return;
    api.get('/legal/operadores')
      .then(setDoc)
      .catch(() => setError(
        'No se pudo abrir el texto. Vuelve a intentarlo en un momento; '
        + 'si sigue sin salir, avisa a quien administra TEGO.'));
  }, [abierto, doc]);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(doc.texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    } catch {
      // SIN PORTAPAPELES NO SE MIENTE. En un navegador que no lo permite
      // —o sin HTTPS— el copiado falla en silencio, y un «¡Copiado!» que
      // no copió se descubre al pegar en el reglamento y encontrarlo
      // vacío. Se dice qué hacer en su lugar.
      setError('Tu navegador no dejó copiar. Selecciona el texto de abajo '
               + 'y cópialo con Ctrl+C.');
    }
  }

  return (
    <div className="border border-gray-200 bg-white rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <FileText className="h-5 w-5 mt-0.5 shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900">
            El aviso que le tienes que dar a tu gente
          </div>
          <p className="text-sm text-gray-600">
            Una terminal registra quién movió material y a qué hora. La ley te
            pide avisarle a tu personal antes de empezar. Aquí tienes el texto
            escrito, para que lo copies a tu reglamento interior.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="text-sm font-semibold text-[var(--color-marca)]"
      >
        {abierto ? 'Ocultar el texto' : 'Ver el texto y copiarlo'}
      </button>

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">
          {error}
        </div>
      )}

      {abierto && !doc && !error && (
        <Cargando filas={4} />
      )}

      {abierto && doc && (
        <>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={copiar}
              className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
            >
              <Copy size={16} />
              Copiar todo el texto
            </button>
            {/* Toda acción confirma visiblemente que ocurrió. */}
            {copiado && (
              <span role="status" className="inline-flex items-center gap-1 text-sm font-semibold text-green-800">
                <Check size={15} /> Copiado
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600">
            <strong>Antes de publicarlo:</strong> cambia lo que está entre
            corchetes por los datos de tu empresa, y pásalo por quien lleve tus
            temas laborales. TEGO no es tu abogado.
          </p>

          {/* Mismo criterio que en la pantalla de acceso: el texto se
              enseña tal cual, sin intérprete de markdown. */}
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-sans text-sm leading-relaxed text-gray-700">
            {doc.texto}
          </pre>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// CONECTAR UNA TERMINAL NUEVA
//
// DOS CAMPOS Y NADA MÁS. El identificador lo pone TEGO, el modo nace en
// «solo mirar», la inactividad en 10 minutos y los tres interruptores
// apagados. Todo eso se puede cambiar después desde la tarjeta — pedirlo
// aquí convertiría «colgar una terminal» en un formulario de ocho campos.
// ---------------------------------------------------------------------
function motivoDeLicencia(licencia) {
  if (licencia === undefined) return null;
  if (licencia === null) return 'No se pudo leer cuántas terminales tienes. Vuelve a intentarlo en un momento.';
  if (licencia.puede_conectar === false) return `${licencia.motivo || ''} ${licencia.que_hacer || ''}`.trim();
  return null;
}

function ConectarTerminal({ onConectada, onError, licencia }) {
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [ubicaciones, setUbicaciones] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    api.get('/ubicaciones').then((u) => setUbicaciones(u || [])).catch(() => {});
  }, [abierto]);

  async function conectar(e) {
    e.preventDefault();
    setGuardando(true);
    try {
      const d = await api.post('/dispositivos/terminales', { nombre, ubicacion });
      onConectada(d);
      setAbierto(false);
      setNombre('');
      setUbicacion('');
    } catch (err) {
      // F1b: con el rechazo por licencia, qué pasó Y qué hacer.
      onError(err instanceof ErrorApi
        ? (err.queHacer ? `${err.message} ${err.queHacer}` : err.message)
        : 'No se pudo conectar.');
    } finally {
      setGuardando(false);
    }
  }

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
      >
        <Plus size={16} />
        Conectar una terminal
      </button>
    );
  }

  const sinDerecho = motivoDeLicencia(licencia);
  const falta = nombre.trim().length < 2 || !ubicacion || !!sinDerecho;

  return (
    <form onSubmit={conectar} className="border border-gray-200 bg-white rounded-xl p-4 space-y-3">
      <div className="font-semibold text-gray-900">Conectar una terminal</div>
      <div>
        <label htmlFor="term-nombre" className="block text-sm font-medium text-gray-700">
          Cómo se llama
        </label>
        <input
          id="term-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Repisa de tornillería"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          El nombre que verás en esta lista. Ponle el de la repisa donde va a
          colgarse, para reconocerla desde el pasillo.
        </p>
      </div>
      <div>
        <label htmlFor="term-ubic" className="block text-sm font-medium text-gray-700">
          En qué repisa se cuelga
        </label>
        <select
          id="term-ubic"
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Elige una…</option>
          {ubicaciones.map((u) => (
            <option key={u.id} value={u.codigo}>{u.codigo} · {u.nombre}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          La terminal solo verá y moverá lo que está en esta repisa. Es lo que
          hace que un ajuste quede atado a un lugar.
        </p>
      </div>
      {/* BOT-1 (19 sep 2026): por qué está gris, a la vista. */}
      <Motivo id="motivo-conectar" cuando={falta && !guardando}>
        {sinDerecho || (nombre.trim().length < 2 ? 'Ponle un nombre a la terminal (mínimo 2 letras).' : 'Elige la repisa donde se cuelga.')}
      </Motivo>
      {licencia?.compradas > 0 && <Ayuda id="terminal.licencia.boton" />}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={falta || guardando}
          aria-describedby={falta ? 'motivo-conectar' : undefined}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {guardando ? 'Conectando…' : 'Conectar y darme el número'}
        </button>
        <button
          type="button"
          onClick={() => setAbierto(false)}
          className="text-sm font-semibold text-gray-600"
        >
          Cancelar
        </button>
        {/* UN BOTÓN APAGADO DICE POR QUÉ. */}
        {falta && !guardando && !sinDerecho && (
          <span className="text-sm text-gray-500">
            Falta ponerle nombre y elegir la repisa.
          </span>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// LOS AJUSTES DE UNA TERMINAL
//
// LOS TRES INTERRUPTORES SE ENSEÑAN CON SU CONSECUENCIA, no con su
// nombre. «permite_ajuste_directo» no le dice nada a nadie; «puede
// cambiar la cantidad ahí mismo, sin conteo» dice qué pasa si lo
// enciendes.
// ---------------------------------------------------------------------
export function Interruptor({ id, encendido, titulo, explicacion, onCambio }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={encendido}
        onChange={(e) => onCambio(e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{titulo}</span>
        <span className="block text-xs text-gray-500">{explicacion}</span>
      </span>
    </label>
  );
}

export function Ajustes({ t, onGuardar }) {
  return (
    <div className="border-t border-gray-200 pt-3 space-y-4">
      <div>
        <label htmlFor={`modo-${t.id}`} className="block text-sm font-medium text-gray-700">
          Qué hace un apretón corto de la perilla
        </label>
        <select
          id={`modo-${t.id}`}
          value={t.modo_principal}
          onChange={(e) => onGuardar(t, { modo_principal: e.target.value },
            `Listo: «${t.nombre}» ahora hace «${nombreDelModo(e.target.value)}».`)}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          {MODOS.map(([codigo, etiqueta]) => (
            <option key={codigo} value={codigo}>{etiqueta}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {explicacionDelModo(t.modo_principal)}
        </p>
      </div>

      <div>
        <label htmlFor={`inact-${t.id}`} className="block text-sm font-medium text-gray-700">
          Minutos sin tocarse antes de cerrarse sola
        </label>
        <input
          id={`inact-${t.id}`}
          type="number"
          min="1"
          max="1440"
          defaultValue={t.inactividad_min}
          onBlur={(e) => {
            const v = Number(e.target.value);
            if (v && v !== t.inactividad_min) {
              onGuardar(t, { inactividad_min: v },
                `Listo: «${t.nombre}» se cerrará sola a los ${v} minutos.`);
            }
          }}
          className="mt-1 w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Cuando pase este rato sin que nadie la use, la terminal se queda solo
          mirando y hay que volver a escanear. En planta 10 está bien; en una
          alacena de casa, 30.
        </p>
      </div>

      <div className="space-y-3">
        <Interruptor
          id={`ajuste-${t.id}`}
          encendido={t.permite_ajuste_directo}
          titulo="Puede cambiar la cantidad ahí mismo"
          explicacion="Sin esto, la terminal solo captura conteos y TEGO calcula la diferencia. Con esto, girar la perilla cambia la existencia en el momento. Nace apagado."
          onCambio={(v) => onGuardar(t, { permite_ajuste_directo: v },
            v ? `«${t.nombre}» ya puede cambiar cantidades directamente.`
              : `«${t.nombre}» vuelve a solo capturar conteos.`)}
        />
        <Interruptor
          id={`reabasto-${t.id}`}
          encendido={t.reabasto_automatico}
          titulo="Avisa sola cuando esta repisa se queda corta"
          /* TC-12. LA EXPLICACIÓN DE ANTES DECÍA «crea la solicitud de
             COMPRA», y eran dos cosas mal: el interruptor no hacía nada,
             y lo que hace ahora no es comprar. Comprar es cuando se
             acaba en toda la empresa; esto es traer de la reserva a esta
             repisa, que es otro trabajo y de otra persona. */
          explicacion="Cuando un giro deja el producto por debajo del mínimo que le pusiste A ESTA REPISA (en Reabastecimiento → Puntos de surtido), TEGO avisa al encargado para que traiga más de la reserva. No compra nada: eso es el aviso de stock bajo, y va aparte. Sin punto de surtido declarado no hace nada. Nace apagado."
          onCambio={(v) => onGuardar(t, { reabasto_automatico: v },
            v ? `«${t.nombre}» avisará sola cuando su repisa se quede corta.`
              : `«${t.nombre}» ya no avisa sola.`)}
        />
        <Interruptor
          id={`gafete-${t.id}`}
          encendido={t.permite_gafete}
          titulo="Acepta gafete además del teléfono"
          explicacion="Para plantas donde no se puede entrar con celular. Necesita el lector de códigos conectado al aparato y gafetes impresos. Nace apagado."
          onCambio={(v) => onGuardar(t, { permite_gafete: v },
            v ? `«${t.nombre}» ya acepta gafete.` : `«${t.nombre}» solo acepta teléfono.`)}
        />
      </div>
    </div>
  );
}

function PedirCodigo({ t, onCodigo, onError }) {
  const [pidiendo, setPidiendo] = useState(false);
  if (!t.activo) return null;
  return (
    <button
      type="button"
      disabled={pidiendo}
      onClick={async () => {
        setPidiendo(true);
        try {
          const d = await api.post(`/dispositivos/${t.id}/codigo`, {});
          onCodigo(d);
        } catch (e) {
          onError(e instanceof ErrorApi ? e.message : 'No se pudo pedir el número.');
        } finally {
          setPidiendo(false);
        }
      }}
      className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
    >
      <KeyRound size={15} />
      {pidiendo ? 'Pidiendo…' : 'Darle otro número'}
    </button>
  );
}

// ---------------------------------------------------------------------
// RETIRAR SOLO LA CREDENCIAL · TC-4
//
// NO ES «RETIRARLA». Retirar apaga la terminal; esto la deja encendida,
// con su nombre, su repisa y su historia, y solo deja sin efecto el
// acceso que tiene ahora. Es lo que se hace si se sospecha que alguien
// copió ese acceso. Para volver a usarla: «Darle otro número».
//
// ACCIÓN RIESGOSA: confirma diciendo la consecuencia en una frase. No se
// deshace con un botón, pero se repara con el de al lado.
// ---------------------------------------------------------------------
export function AvisoCredencialRetirada({ t }) {
  if (!t.activo || !t.credencial_retirada_en) return null;
  return (
    <div role="status" className="mt-1 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
      Su acceso se retiró el {fechaYHora(t.credencial_retirada_en)}. No entra
      a TEGO hasta que pulses «Darle otro número» y lo teclees en el aparato:
      mantén apretada la perilla 3 segundos para teclearlo.
    </div>
  );
}

export function RetirarCredencial({ t, onHecho, onError, confirmandoInicial = false }) {
  const [confirmando, setConfirmando] = useState(confirmandoInicial);
  const [enviando, setEnviando] = useState(false);
  if (!t.activo) return null;

  async function retirar() {
    setEnviando(true);
    try {
      const d = await api.post(`/dispositivos/terminales/${t.id}/retirar-credencial`, {});
      setConfirmando(false);
      onHecho(d);
    } catch (e) {
      onError(e instanceof ErrorApi ? e.message : 'No se pudo retirar el acceso. Revisa tu conexión y vuelve a intentarlo.');
    } finally {
      setEnviando(false);
    }
  }

  if (!confirmando) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => setConfirmando(true)}
          className="inline-flex items-center gap-1.5 border border-amber-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-amber-800"
        >
          <ShieldOff size={15} />
          Retirar su acceso
        </button>
        <Ayuda id="terminal.terminales.retirar_credencial" />
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-sm">
      <span className="text-gray-700">
        Este aparato dejará de funcionar hasta que lo vuelvas a conectar con
        un número nuevo: en el aparato, mantén apretada la perilla 3 segundos
        para teclearlo. Su historia se conserva.
      </span>
      <button
        type="button"
        disabled={enviando}
        onClick={retirar}
        className="inline-flex items-center gap-1.5 bg-amber-600 text-white rounded-lg px-3 py-1.5 font-semibold disabled:opacity-50"
      >
        <Check size={15} />
        {enviando ? 'Retirando…' : 'Sí, retirar su acceso'}
      </button>
      <button type="button" disabled={enviando} onClick={() => setConfirmando(false)} className="font-semibold text-gray-600">
        No
      </button>
    </span>
  );
}

// ACCIÓN RIESGOSA: pide confirmación EXPLICANDO LA CONSECUENCIA en una
// frase, y se puede deshacer (volver a conectarla es un botón).
export function Retirar({ t, onGuardar }) {
  const [confirmando, setConfirmando] = useState(false);

  if (!t.activo) {
    return (
      <button
        type="button"
        onClick={() => onGuardar(t, { activo: true }, `«${t.nombre}» vuelve a estar conectada.`)}
        className="inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700"
      >
        <Power size={15} />
        Volver a conectarla
      </button>
    );
  }

  if (!confirmando) {
    return (
      <button
        type="button"
        onClick={() => setConfirmando(true)}
        className="inline-flex items-center gap-1.5 border border-red-200 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-red-700"
      >
        <Power size={15} />
        Retirarla
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className="text-gray-700">
        Dejará de mover inventario ahora mismo y quien esté usándola se
        quedará fuera. Lo que ya registró se queda.
      </span>
      <button
        type="button"
        onClick={() => { setConfirmando(false); onGuardar(t, { activo: false }, `«${t.nombre}» quedó retirada.`); }}
        className="inline-flex items-center gap-1.5 bg-red-600 text-white rounded-lg px-3 py-1.5 font-semibold"
      >
        <Check size={15} />
        Sí, retirarla
      </button>
      <button type="button" onClick={() => setConfirmando(false)} className="font-semibold text-gray-600">
        No
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------
// LA TERMINAL DE PRODUCTO EN SU TARJETA · F1 (DISEÑO-F1 §7, contrato v2 §8)
//
// LOS CAMPOS SON OPCIONALES en `GET /dispositivos/terminales` —`serie`,
// `firmware`, `codigo_terminal`, `vinculada_en`, `robada_en`—: una
// tarjeta sin `serie` se ve exactamente como
// hoy. Las tres acciones siguen el patrón de `RetirarCredencial`: el
// botón dice con palabras lo que hace y la confirmación dice la
// consecuencia en una frase, nunca «¿estás seguro?».
// ---------------------------------------------------------------------
const esDeProducto = (t) => !!t?.serie;
const estaRobada = (t) => !!t?.robada_en;

function textoDeError(e) {
  const { mensaje, queHacer } = leerErrorDelContrato(e);
  return `${mensaje} ${queHacer}`;
}

export function DatosDeProducto({ t }) {
  if (!esDeProducto(t)) return null;
  const partes = [`Serie ${t.serie}`];
  if (t.firmware) partes.push(`firmware ${t.firmware}`);
  if (t.codigo_terminal) partes.push(`código ${t.codigo_terminal}`);
  return (
    <div className="text-sm text-gray-600 break-words">
      <div>{partes.join(' · ')}</div>
      {estaRobada(t) && (
        <div role="status" className="mt-1 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
          Marcada como robada el {fechaYHora(t.robada_en)}: su serie está bloqueada en
          todo TEGO y no entra. Si aparece, pulsa «Apareció».
        </div>
      )}
    </div>
  );
}

/** Botón + confirmación con la consecuencia, en línea. */
function AccionConConsecuencia({ icono: Icono, rotulo, consecuencia, confirmar, ayuda, tono = 'ambar', hacer, onHecho, onError }) {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const borde = tono === 'rojo' ? 'border-red-200 text-red-700' : tono === 'verde' ? 'border-green-300 text-green-800' : 'border-amber-300 text-amber-800';
  const fondo = tono === 'rojo' ? 'bg-red-600' : tono === 'verde' ? 'bg-green-700' : 'bg-amber-600';

  async function ejecutar() {
    setEnviando(true);
    try {
      const d = await hacer();
      setConfirmando(false);
      onHecho(d || {});
    } catch (e) {
      onError(textoDeError(e));
    } finally {
      setEnviando(false);
    }
  }

  if (!confirmando) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => setConfirmando(true)}
          className={`inline-flex min-h-11 items-center gap-1.5 border bg-white rounded-lg px-3 py-1.5 text-sm font-semibold ${borde}`}
        >
          <Icono size={15} aria-hidden="true" />
          {rotulo}
        </button>
        {ayuda}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-sm">
      <span className="text-gray-700 break-words">{consecuencia}</span>
      <button
        type="button"
        disabled={enviando}
        onClick={ejecutar}
        className={`inline-flex min-h-11 items-center gap-1.5 ${fondo} text-white rounded-lg px-3 py-1.5 font-semibold disabled:opacity-50`}
      >
        <Check size={15} aria-hidden="true" />
        {enviando ? 'Un momento…' : confirmar}
      </button>
      <button type="button" disabled={enviando} onClick={() => setConfirmando(false)} className="min-h-11 font-semibold text-gray-600">
        No
      </button>
    </span>
  );
}

export function AccionesDeProducto({ t, onHecho, onError, puedeTraspasar = false }) {
  if (!esDeProducto(t) || !t.activo) return null;
  if (estaRobada(t)) {
    return (
      <AccionConConsecuencia
        icono={Check}
        rotulo="Apareció"
        tono="verde"
        consecuencia="Su serie deja de estar bloqueada y podrás volver a vincularla con un código nuevo."
        confirmar="Sí, apareció"
        hacer={() => api.post(`/terminales/${t.id}/aparecio`, {})}
        onHecho={(d) => onHecho({ mensaje: d?.mensaje || `«${t.nombre}» ya no está marcada como robada.` })}
        onError={onError}
      />
    );
  }
  return (
    <>
      {t.vinculada_en && (
        <AccionConConsecuencia
          icono={Unlink}
          rotulo="Desvincular"
          consecuencia="Dejará de entrar en su siguiente petición y borrará lo que tenía guardado; lo capturado sin enviar se guarda aparte 24 horas por si la vuelves a vincular aquí. Su historia se conserva."
          confirmar="Sí, desvincular"
          ayuda={<Ayuda id="terminal.terminales.desvincular" />}
          hacer={() => api.post(`/terminales/${t.id}/desvincular`, { motivo: 'Desvinculada desde Terminales' })}
          onHecho={(d) => onHecho({ mensaje: d?.mensaje || `«${t.nombre}» quedó desvinculada.` })}
          onError={onError}
        />
      )}
      {/* UNA ACCIÓN NORMAL DE LA TARJETA, no un aviso (contrato 2.3.1 §8,
          auditoría F1 H-1): la terminal ya no le enciende nada a la app
          al pedir código estando vinculada, así que quien quiere
          reinstalarla lo hace desde aquí cuando lo decide. */}
      {t.vinculada_en && (
        <AccionConConsecuencia
          icono={RotateCcw}
          rotulo="Desvincular y volver a vincular"
          consecuencia="Se le quita el acceso ahora. En cuanto vuelva a pedir código, lo verás aquí con su sucursal, repisa y nombre ya puestos, para aprobarla en un toque. Lo que ya registró se queda."
          confirmar="Sí, volver a vincular"
          ayuda={<Ayuda id="terminal.terminales.revincular" />}
          hacer={() => api.post(`/terminales/${t.id}/desvincular`, { revincular: true, motivo: 'Volver a vincular desde Terminales' })}
          onHecho={(d) => onHecho({ mensaje: d?.mensaje || `«${t.nombre}» quedó desvinculada. Cuando vuelva a pedir código, apruébala aquí.` })}
          onError={onError}
        />
      )}
      {t.via_licencia === 'COMPRA' && puedeTraspasar && (
        <PasarAOtraEmpresa t={t} onHecho={onHecho} onError={onError} />
      )}
      <AccionConConsecuencia
        icono={ShieldAlert}
        rotulo="Marcar como robada"
        tono="rojo"
        consecuencia="Dejará de entrar en su siguiente petición y borrará lo que tenía guardado; lo capturado sin enviar se guarda aparte para que un encargado lo revise. Su serie queda bloqueada y a quienes la usaron en 7 días se les pedirá cambiar su PIN."
        confirmar="Sí, marcarla como robada"
        ayuda={<Ayuda id="terminal.terminales.robada" />}
        hacer={() => api.post(`/terminales/${t.id}/robada`, {})}
        onHecho={(d) => onHecho({ mensaje: d?.mensaje || `«${t.nombre}» quedó marcada como robada: su serie está bloqueada.` })}
        onError={onError}
      />
    </>
  );
}


// F1c (D10): PASAR A OTRA EMPRESA. Solo una terminal comprada y solo quien
// tiene `terminal.traspasar` (el DUEÑO). Confirmación escribiendo su nombre;
// después, el código nuevo UNA vez, para dárselo a quien la recibe.
export function PasarAOtraEmpresa({ t, onHecho, onError }) {
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [codigo, setCodigo] = useState(null);
  const coincide = nombre.trim() === (t.nombre || '').trim();

  async function pasar() {
    setEnviando(true);
    try {
      const d = await api.post(`/terminales/${t.id}/traspasar`, { confirmacion: nombre.trim() });
      setCodigo(d.codigo);
    } catch (e) {
      onError?.(e);
    } finally {
      setEnviando(false);
    }
  }

  if (codigo) {
    return (
      <div role="status" className="space-y-2 rounded-lg border border-green-300 bg-green-50 p-3" data-terminal="traspaso-codigo">
        <p className="text-sm text-gray-800">«{t.nombre}» salió de tu empresa. Código para quien la recibe:</p>
        <p className="font-mono text-2xl font-bold tracking-widest">{codigo}</p>
        <p className="text-sm text-gray-700">Cópialo o anótalo: no se vuelve a mostrar.</p>
        <button type="button" className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-3"
                onClick={() => onHecho?.({ mensaje: `«${t.nombre}» salió de tu empresa.` })}>
          Ya lo anoté
        </button>
      </div>
    );
  }
  if (!abierto) {
    return (
      <span className="inline-flex items-center gap-1">
        <button type="button" onClick={() => setAbierto(true)}
                className="min-h-[44px] rounded-lg border border-amber-300 bg-white px-3 text-sm">
          Pasar a otra empresa
        </button>
        <Ayuda id="terminal.activacion.traspasar" />
      </span>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm text-gray-800">
        La terminal sale de tu empresa y deja de trabajar aquí. Recibirás un código nuevo para quien la reciba.
      </p>
      <label className="block text-sm">
        Escribe su nombre para confirmar: <strong>{t.nombre}</strong>
        <input className="entrada mt-1 w-full" value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </label>
      <Motivo id={`motivo-traspaso-${t.id}`} cuando={!coincide && !enviando}>
        Escribe el nombre de la terminal tal cual para confirmar.
      </Motivo>
      <div className="flex gap-2">
        <button type="button" onClick={pasar} disabled={!coincide || enviando}
                aria-describedby={!coincide ? `motivo-traspaso-${t.id}` : undefined}
                className="min-h-[44px] rounded-lg bg-red-600 px-3 font-semibold text-white disabled:opacity-50">
          {enviando ? 'Un momento…' : 'Sí, pasarla a otra empresa'}
        </button>
        <button type="button" onClick={() => { setAbierto(false); setNombre(''); }}
                className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-3">
          Cancelar
        </button>
      </div>
    </div>
  );
}
